import { useMemo, useState } from "react";
import { arrayOf, bool, number, object, shape, string } from "prop-types";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { PercentSettingContents } from "@tools";
import { Accordion, Alert, Dialog } from "@components";
import { BufferTitle } from "./BufferTitle";
import classnames from "classnames";
import { capitalize } from "@material-ui/core";

export const BufferEntry = observer(({
  buffer,
  readOnly,
  isFirstOfLevel,
  availableWorkTypes,
}) => {
  const { t } = useTranslation();
  
  const [ expanded, setExpanded ] = useState(false);
  const [ alertVisible, showAlert ] = useState(false);
  const [ removeAlertVisible, showRemoveAlert ] = useState(false);
  
  const {
    id,
    lvl,
    used,
    name,
    percent,
    workTypes
  } = buffer;
  
  const messageContent = useMemo(() => {
    if(!readOnly)
      return;
    if(lvl === -1)
      return t("views.editor.dialogs.buffers.alert.project");
    if(lvl >= 0)
      return t("views.editor.dialogs.buffers.alert.item", { levelName: t(`proposal_parts.module_levels.level_${lvl}`) })
  }, [ readOnly, lvl ])
  
  const messageTitle = useMemo(() => (
    readOnly
      ? t("views.editor.dialogs.buffers.name_full", { levelName: capitalize(t(`proposal_parts.module_levels.level_${lvl}`)) })
      : undefined
  ), [ readOnly, lvl ])
  
  const handleExpand = (e) => {
    if(e.target.nodeName === "INPUT")
      return;
    setExpanded(!expanded)
  }
  
  const handleShowAlert = (e) => {
    e?.stopPropagation?.();
    showAlert(true);
  }
  
  const handleSetWorkTypes = (workTypes) => {
    if(readOnly) {
      showAlert(true);
      return;
    }
    buffer.setWorkTypes(workTypes)
  }
  
  const handleRemove = (e) => {
    e.stopPropagation();
    if(readOnly)
      showAlert(true);
    else
      showRemoveAlert(true);
  }
  
  const removeBuffer = () => {
    buffer.removeSelf(id);
  }
  
  return (
    <>
      <Accordion
        expanded={expanded}
        onExpand={handleExpand}
        title={<BufferTitle
          name={name}
          used={used}
          lvl={lvl}
          expanded={expanded}
          isFirstOfLevel={isFirstOfLevel}
          percent={percent}
          setUsed={buffer.setUseState}
          setName={buffer.setName}
          onNameInputFocus={readOnly ? handleShowAlert : undefined}
          onRemove={handleRemove}
          readOnly={readOnly}
        />}
        className="my-0-5"
        titleClassName="pl-2"
        titleColored
      >
        <PercentSettingContents
          percent={percent}
          usedWorkTypes={workTypes.slice()}
          availableWorkTypes={availableWorkTypes}
          onPercentChange={buffer.setPercent}
          onSelectWorkType={handleSetWorkTypes}
          clickDialogMessageTitle={messageTitle}
          clickDialogMessageContent={messageContent}
          showOnlyUsedWorkTypes={readOnly}
          showPercentageWorkTypesTooltip
          className={classnames(readOnly && "transparent-2")}
          hideWorkTypeList
          allowNegative
          allowSelectAllWorkTypes
        />
      </Accordion>
      {
        readOnly && alertVisible &&
        <Dialog
          open
          onClose={() => showAlert(!alertVisible)}
          title={messageTitle}
        >
          {messageContent}
        </Dialog>
      }
      {
        removeAlertVisible &&
        <Alert
          isOpen
          title={t("views.editor.dialogs.buffers.remove")}
          acceptText={t("common.yes")}
          onAccept={removeBuffer}
          onCancel={() => showRemoveAlert(false)}
        />
      }
    </>
  );
});

BufferEntry.propTypes = {
  buffer: object.isRequired,
  availableWorkTypes: arrayOf(shape({
    id: number.isRequired,
    name: string.isRequired,
    percentageWorkTypes: arrayOf(shape({
      id: number.isRequired,
      name: string,
    }))
  })),
  isFirstOfLevel: bool,
  readOnly: bool,
};